<script setup lang="ts">
import { useToaster } from "ram-components";
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { type FormKitNode } from "@formkit/core";
import { email } from "@formkit/inputs";
import services from "@/services";
import { encryptPassword } from "@/helper/passwordEncryptor";
import Button from "@/components/Button.vue";


const route = useRoute();
const router = useRouter();
const isSubmitting = ref(false);

const token = route.query.token as string;

const toaster = useToaster({
  position: "bottom-right",
  closeOnClick: false,
  pauseOnHover: true,
  showCloseOnHover: true,
  timeout: 6000,
});

const hasCapital = (node: FormKitNode) => {
  return /[A-Z]/.test(node.value as string);
};

const hasTwoNumbers = (node: FormKitNode) => {
  const matches = (node.value as string).match(/\d/g);
  return (matches?.length ?? 0) >= 2;
};
const hasSymbol = (node: FormKitNode) => {
  return /[^a-zA-Z0-9]/.test(node.value as string);
};

async function resetPassword(formData: any, node: any) {
  node.clearErrors();

  try {
    isSubmitting.value = true;
const encryptedPass = await encryptPassword(formData.password);
    const payload = {
      token: token,
      emailID: formData.email,
      password: encryptedPass,
    };

    const response = await services.activateUser(payload);
    toaster.push({
      type: "success",
      text: "Password set Successfully!",
    });

    node.reset();

    setTimeout(() => router.push("/login"), 1000);
  } catch (error: any) {
    toaster.push({
      type: "error",
      text: error.response?.data?.message || "An error Occured!",
    });
  }finally{
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div
    class="relative flex min-h-screen items-center justify-center bg-gray-100 p-4"
  >
    <div class="absolute right-4 top-0 z-50">
      <img src="/src/assets/img/ram-logo.png" alt="Logo" class="h-14 w-auto" />
    </div>

    <div class="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg">
      <div class="mb-4 flex justify-center">
        <img
          src="/src/assets/img/sb_logo.png"
          alt="SB Logo"
          class="h-16 w-auto"
        />
      </div>

      <h3 class="text-center font-semibold">SBSA Pick Up Point</h3>

      <div v-if="token">
        <h2 class="mb-6 mt-2 text-center text-2xl font-semibold text-gray-800">
          Set a new Password
        </h2>

        <FormKit
          type="form"
          submit-label="Submit"
          :actions="false"
          @submit="resetPassword"
          :incomplete-message="false"
        >
          <FormKit
            label="Email"
            name="email"
            type="email"
            validation="required"
            :classes="{
              messages: 'text-red-600 text-xs mt-1',
              inputInvalid: 'border-red-500 focus:ring-red-500',
            }"
            validation-visibility="live"
            label-class="block text-sm font-medium text-gray-700 mb-1"
            input-class="w-full px-4 py-2.5 border border-gray-300 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-sbsa-blue focus:border-transparent"
          />
          <FormKit
            type="password"
            name="password"
            label="Password"
            :validation-rules="{ hasCapital, hasSymbol, hasTwoNumbers }"
            validation="required|length:8|hasCapital|hasTwoNumbers|hasSymbol"
            :validation-messages="{
              length: 'Password must be at least 8 characters.',
              hasCapital: 'Must contain at least one UPPERCASE letter.',
              hasTwoNumbers: 'Must contain at least 2 numbers.',
              hasSymbol: 'Must contain at least 1 symbol (e.g., !@#$%).',
            }"
            :classes="{
              messages: 'text-red-600 text-xs mt-1',
              inputInvalid: 'border-red-500 focus:ring-red-500',
            }"
            validation-visibility="live"
            label-class="block text-sm font-medium text-gray-700 mb-1"
            input-class="w-full px-4 py-2.5 border border-gray-300 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-sbsa-blue focus:border-transparent"
          />

          <FormKit
            type="password"
            name="password_confirm"
            label="Confirm password"
            validation="required|confirm"
            :classes="{
              messages: 'text-red-600 text-xs mt-1',
              inputInvalid: 'border-red-500 focus:ring-red-500',
            }"
            validation-visibility="live"
            validation-label="Password confirmation"
            label-class="block text-sm font-medium text-gray-700 mb-1"
            input-class="w-full px-4 py-2.5 border border-gray-300 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-sbsa-blue focus:border-transparent"
          />

          <div class="mt-6">
            <Button
            :loading="isSubmitting"
            variant="ram"
            class="w-full"
            type="submit"
            >
            Submit
            </Button>
          </div>
        </FormKit>
      </div>
      <div
        v-else
        class="my-3 items-center rounded-lg bg-red-300 p-4 text-center text-lg font-bold text-ram-red"
      >
        Invalid link or Missing Token!
      </div>
    </div>
  </div>
</template>
